import { AdminFeature } from '@website/components/admin/AdminFeature';
import MediaSelectorField from '@website/components/forms/fields/MediaSelectorField';
import UploadMediaToTransLoadIt from '@website/components/media/UploadMediaToTransLoadIt';
import CardReview from '@website/components/trips/TripsPage/reviews/CardReview';
import {
  ReviewSource,
  Trip,
  UpdateReviewInput,
  UpdateReviewMutation,
  UserGuestOnBookingsQuery,
} from '@website/types/graphqlOperations';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { Button, Form, OverlayTrigger } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FaCheckCircle, FaCopy } from 'react-icons/fa';
import StarsField from '../../forms/fields/StarsField';
import { SubmitButton } from '../../forms/fields/SubmitButton';

export function HeadGuidesText({
  team,
}: {
  team: UserGuestOnBookingsQuery['user']['guestOnBookings']['items'][0]['group']['trip']['host']['team'];
}) {
  if (team.items.length === 0) {
    return null;
  }

  if (team.items.length === 1) {
    return (
      <p>
        It will mean a lot to head guide {team.items[0]?.givenName} and their
        team!
      </p>
    );
  }

  return (
    <p>
      It will mean a lot to head guides {team.items[0]?.givenName},{' '}
      {team.items[1]?.givenName} and their team!
    </p>
  );
}

export default function ReviewTripForm({
  onSubmit,
  isLoading,
  values,
  space,
  givenName,
  userID,
  trip,
  review,
}: {
  onSubmit: (values: UpdateReviewInput) => void;
  isLoading?: boolean;
  values?: UpdateReviewInput;
  space?: UserGuestOnBookingsQuery['user']['guestOnBookings']['items'][0];
  givenName?: string;
  userID?: string;
  trip?: Pick<Trip, 'id' | 'title'>;
  review?: UpdateReviewMutation['updateReview'];
}): JSX.Element {
  const reviewSource = trip?.id ? ReviewSource.EXTERNAL : ReviewSource.GUEST;

  const [copied, setCopied] = useState(false);
  const thanksRef = useRef<HTMLDivElement>(null);

  const {
    control,
    formState: { errors, isSubmitting, isSubmitted },
    getValues,
    handleSubmit,
    register,
  } = useForm<UpdateReviewInput>({
    mode: 'onBlur',
    defaultValues: {
      ...values,
      ...(values?.rating && { rating: values.rating * 5 }),
      source: reviewSource,
      tripID: trip?.id as string,
    },
  });

  const submitReviewForm = async (formData: UpdateReviewInput) => {
    await onSubmit({ ...formData });
    window.scrollTo({
      top: 0,
    });
  };

  if (isSubmitted) {
    return (
      <>
        {reviewSource === ReviewSource.GUEST ? (
          <>
            <h1 ref={thanksRef}>Thanks for reviewing {givenName}!</h1>

            {getValues('rating') > 4 ? (
              <>
                <p>
                  Want to <strong>earn $20 in Skyhook credits</strong> to use
                  against the deposit of your next trip?
                </p>

                <p>Leave us a Trustpilot review!</p>

                <ol>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                  <li
                    role="button"
                    onClick={() => {
                      navigator.clipboard.writeText(review.body);
                      setCopied(true);
                    }}
                  >
                    Click to{' '}
                    <span className="text-primary">copy your review</span>{' '}
                    {copied ? (
                      <>
                        <FaCheckCircle className="text-success" />
                      </>
                    ) : (
                      <FaCopy />
                    )}
                  </li>
                  <OverlayTrigger placement="top" overlay={<>Click to copy</>}>
                    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                    <p
                      className="col-12 col-md-8 col-lg-6 mt-2 bg-light rounded"
                      role="button"
                      onClick={() => {
                        navigator.clipboard.writeText(review.body);
                        setCopied(true);
                      }}
                    >
                      {review && <CardReview review={review} />}
                    </p>
                  </OverlayTrigger>
                  <li>
                    Paste and publish your review on{' '}
                    <a
                      href="https://uk.trustpilot.com/evaluate/skyhookadventure.com?utm_medium=trustbox&utm_source=TrustBoxReviewCollector"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Trustpilot
                    </a>
                    .
                  </li>
                  <li>
                    Drop us an email to let us know you’ve left a review. We’ll
                    add the credits to your dashboard.
                  </li>
                </ol>
                <Button
                  href="https://uk.trustpilot.com/evaluate/skyhookadventure.com?utm_medium=trustbox&utm_source=TrustBoxReviewCollector"
                  target="_blank"
                  variant="success"
                  style={{ color: '#fff' }}
                >
                  Review us on Trustpilot
                </Button>
              </>
            ) : (
              <>
                We are always striving for excellence and your insights help us
                improve. We've passed on your feedback to the local guides who
                ran your trip.
              </>
            )}

            <div>
              <Link href={`/me/${userID}`} legacyBehavior passHref>
                <Button href="/me" className="mt-1">
                  Back to dashboard
                </Button>
              </Link>
            </div>
          </>
        ) : (
          <h1>Review for {trip.title} submitted successfully!</h1>
        )}
      </>
    );
  }

  return (
    <>
      <h1>
        {reviewSource === ReviewSource.EXTERNAL ? (
          <>Submit review for {trip.title}</>
        ) : (
          <>How was your trip {givenName}?</>
        )}
      </h1>
      <Form role="form" onSubmit={handleSubmit(submitReviewForm)}>
        <Form.Group className="mb-3">
          <Form.Label
            id="rating"
            className="d-block mt-3"
            style={{ marginBottom: -5 }}
          >
            Rating *
          </Form.Label>
          <StarsField control={control} name="rating" required />
        </Form.Group>

        <Form.Group controlId="body" className="mb-3">
          <Form.Label>Review *</Form.Label>
          <Form.Control
            as="textarea"
            required
            {...register('body', { required: true })}
            isInvalid={!!errors.body}
            rows={6}
          />
        </Form.Group>

        {reviewSource === ReviewSource.EXTERNAL ? (
          <Form.Group className="mb-3">
            <Form.Label className="mb-1 d-block">Photos</Form.Label>
            <MediaSelectorField
              control={control}
              userID={userID}
              name="media"
              allowMultipleSelect
            />
          </Form.Group>
        ) : (
          <Form.Group className="mb-3">
            <Form.Label className="mb-1 d-block">Photos</Form.Label>
            <Form.Text className="mb-2 d-block">
              If you've already uploaded photos to your booking, they will be
              added automatically (so you don't need to add them here again).
            </Form.Text>
            <UploadMediaToTransLoadIt
              groupID={space?.group?.id}
              tripID={space?.group?.trip?.id}
              allowMultipleSelect
              userID={userID}
            />
          </Form.Group>
        )}

        {reviewSource === ReviewSource.EXTERNAL && (
          <AdminFeature>
            <Form.Group className="mb-3">
              <Form.Label className="mb-1 d-block">Given Name</Form.Label>
              <Form.Control
                type="text"
                required
                {...register('givenName', { required: true })}
                isInvalid={!!errors.givenName}
              />
              <Form.Text className="mb-2 d-block">
                The given name of the person who reviewed the trip externally.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="mb-1 d-block">Reviewed on</Form.Label>
              <Form.Control
                type="date"
                required
                {...register('starts', { required: true })}
                isInvalid={!!errors.starts}
              />
              <Form.Text className="mb-2 d-block">
                The date the trip was reviewed externally.
              </Form.Text>
            </Form.Group>
          </AdminFeature>
        )}

        <SubmitButton
          text="Submit"
          submittingLabel="Submitting"
          isSubmitting={isLoading || isSubmitting}
        />
      </Form>
    </>
  );
}
